import { GUIA_LABELS } from '@utils/guiaLabels.js'
import { useNavigate } from 'react-router-dom'
import { useProximoPasso } from '@hooks/useProximoPasso.js'
import { useChecklistGeral } from '@hooks/useChecklistGeral.js'
import { useTopico } from '@hooks/useTopico.js'
import { getTopicoContent } from '@content/topicos-content.js'
import { topicoDetailPath, ROUTES } from '@utils/constants.js'
import { Button } from '@components/primitives/Button.js'
import { PassoAtualSkeleton } from '@components/primitives/Skeleton.js'
import { PageShell } from '@components/layout/PageShell.js'
import { computeAlertaSequencia } from '@utils/alertaSequencia.js'
import { getPassoByTopicoId } from '@content/jornada.js'
import { PassoBadgeList } from '@components/guia/PassoBadgeList.js'
import { PassoEstatisticasPanel } from '@components/guia/PassoEstatisticasPanel.js'
import { formatPassoOrdem } from '@utils/passoLabel.js'
import { useChecklistPendentes } from '@hooks/useChecklistPendentes.js'
import { computePassoEstatisticas } from '@utils/passoEstatisticas.js'
import { getTopicoTipo } from '@utils/topicoTipo.js'

export function PassoAtualPage() {
  const navigate = useNavigate()
  const proximo = useProximoPasso()
  const { modulos } = useChecklistGeral()
  const { totalItens } = useChecklistPendentes()
  const topicoHook = useTopico(proximo?.moduloId ?? '', proximo?.topicoId ?? '')

  if (!proximo) {
    return (
      <PageShell variant='guia-hero'>
        <div className='cf3-passo-hero cf3-passo-hero--done'>
          <h1 className='cf3-passo-hero__title'>Jornada concluída</h1>
          <p className='cf3-passo-hero__list cf3-passo-hero__list--muted'>
            Você concluiu todos os módulos do guia. Use o menu Obra para acompanhar financeiro e
            documentos.
          </p>
          {totalItens > 0 && (
            <div className='cf3-progresso__alerta cf3-passo-resumo__pendencias' role='status'>
              <p className='cf3-progresso__alerta-title'>Checklists do guia</p>
              <p className='cf3-progresso__alerta-text'>
                Ainda há {totalItens} {totalItens === 1 ? 'item' : 'itens'} de checklist sem marcar
                em etapas anteriores.
              </p>
            </div>
          )}
          <div className='cf3-actions'>
            {totalItens > 0 && (
              <Button variant='primary' onClick={() => navigate(ROUTES.CHECKLIST_PENDENTES)}>
                Ver checklists do guia
              </Button>
            )}
            <Button
              variant={totalItens > 0 ? 'secondary' : 'primary'}
              onClick={() => navigate(ROUTES.HOME)}
            >
              Ir para o início
            </Button>
          </div>
        </div>
      </PageShell>
    )
  }

  if (topicoHook.isLoading) {
    return (
      <PageShell variant='guia-hero'>
        <PassoAtualSkeleton label='Carregando etapa…' />
      </PageShell>
    )
  }

  const { content, progresso, diario, checklistItems } = topicoHook
  const alertaPert = computeAlertaSequencia(modulos)
  const passoJornada = getPassoByTopicoId(proximo.topicoId)
  const moduloProgress = modulos.find((m) => m.id === proximo.moduloId)
  const tipo = getTopicoTipo(proximo.moduloId, proximo.topicoSlug, proximo.topicoId)
  const temFerramenta = tipo === 'ferramenta' || (content?.temFerramenta ?? false)
  const stats = computePassoEstatisticas({
    content: content ?? getTopicoContent(proximo.topicoId) ?? null,
    progresso,
    checklistItems,
    diario,
    temFerramenta,
    topicoId: proximo.topicoId,
  })

  return (
    <PageShell variant='guia-hero'>
      {alertaPert && (
        <div className='cf3-progresso__alerta' role='alert'>
          <p className='cf3-progresso__alerta-title'>Ordem recomendada</p>
          <p className='cf3-progresso__alerta-text'>{alertaPert}</p>
        </div>
      )}

      <div className='cf3-passo-resumo'>
        <p className='cf3-passo-resumo__overline'>{proximo.moduloTitulo}</p>
        <h1 className='cf3-passo-resumo__title'>{proximo.topicoTitulo}</h1>
        {passoJornada && <PassoBadgeList passo={passoJornada} compact />}

        <PassoEstatisticasPanel
          stats={stats}
          {...(moduloProgress?.pct != null ? { moduloPct: moduloProgress.pct } : {})}
          {...(passoJornada ? { passoOrdem: formatPassoOrdem(passoJornada) } : {})}
        />

        {totalItens > 0 && (
          <div className='cf3-progresso__alerta cf3-passo-resumo__pendencias' role='status'>
            <p className='cf3-progresso__alerta-title'>Checklists para trás</p>
            <p className='cf3-progresso__alerta-text'>
              {totalItens} {totalItens === 1 ? 'item pendente' : 'itens pendentes'} em etapas
              anteriores.
            </p>
            <Button
              variant='secondary'
              size='sm'
              className='cf3-passo-resumo__pendencias-btn'
              onClick={() => navigate(ROUTES.CHECKLIST_PENDENTES)}
            >
              Ver checklists do guia
            </Button>
          </div>
        )}

        <div className='cf3-actions'>
          <Button
            variant='primary'
            onClick={() => navigate(topicoDetailPath(proximo.moduloId, proximo.topicoId))}
          >
            {GUIA_LABELS.continuarEtapa}
          </Button>
        </div>
      </div>
    </PageShell>
  )
}
